import React, { useContext, useEffect, useState } from 'react'
import { CustomerContext } from '../../../contexts/CustomerContext'
import PackageItem from '../../PackageItem'
import styles from './PackageSelection.module.sass'
import Grid from '../../Grid'
import Typography from '../../Typography'
import { Package, StudentImage } from '../../../models/_models'
import useQueryString from '../../../hooks/use-query-string'
import { routes } from '../../../routes'
import { usePoses } from '../_ElementaryMultiPose/MultiPosePackageBuilder/hooks'
import useLocalStorage from "../../../hooks/useLocalStorage";
import classes from "../Sittings/Sittings.module.sass";
import Spinner from "../../Spinner";
import prevYearsBanner from '../../../assets/prev_years.png'
import OrderPackage from "../../../models/OrderPackage";
import { Pose } from "../../../models";
import { getPreviousOrders } from "../../../api/previous_orders";
import PreviousYears from './PreviousYears';
import usePastImagesQuickOrderPricelist from '../../../hooks/usePastImagesQuickOrderPricelist';

const CATEGORY_WEIGHTS = {
  normal: 0,
  gift: 1
};

const PURCHASED_CATEGORY_WEIGHTS = {
  gift: 0,
  normal: 1,
};

const PackageSelection = () => {
  const { pid, senior } = useQueryString()
  const { customer, setCustomer } = useContext(CustomerContext)
  const { currentStudent } = customer
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [studentImages, setStudentImages] = useState([])
  const [displayPastImages, setDisplayPastImages] = useState(false)
  const [fetchingPastImages, setFetchingPastImages] = useState(true)
  const [currentStudentImage, setCurrentStudentImage] = useState()
  const { fetching, data: poseData } = usePoses(currentStudent.id, packages.map((pack) => pack.id))
  const { fetching: isFetchingPreviousOrders, data: hasPreviousOrders } = getPreviousOrders(currentStudent.id)
  const [isUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const [isOrderingAhead] = useLocalStorage('orderingAhead')
  const [currentSeason] = useLocalStorage(`currentSeason-${currentStudent.id}`)
  const { data: pastImagesQuickOrderPricelist } = usePastImagesQuickOrderPricelist(customer.currentSchool && customer.currentSchool.id)
  const showPastImagesCard = !!pastImagesQuickOrderPricelist && displayPastImages && !isOrderingAhead && !pid && !senior

  const pricelistSort = (a, b) => {
    if (hasPreviousOrders) {
      if (PURCHASED_CATEGORY_WEIGHTS[a.category] < PURCHASED_CATEGORY_WEIGHTS[b.category]) return -1;
    } else {
      if (CATEGORY_WEIGHTS[a.category] < CATEGORY_WEIGHTS[b.category]) return -1;
    }

    return 1;
  }

  useEffect(() => {
    StudentImage
      .where({ studentId: currentStudent.id })
      .all()
      .then((response) => {
        setStudentImages(response.data)
        setCurrentStudentImage(response.data.find(image => image.folder === currentSeason))
      })
  }, [])

  useEffect(() => {
    const preorderParam = isOrderingAhead ? { season: { match: 'fall2023' } } : {}
    Package
      .includes({ backgroundSet: 'backgrounds' })
      .where({ schoolId: customer.currentSchool.id })
      .where({ has_prices_for_school: customer.currentSchool.id })
      .where(preorderParam)
      .where({ notPastImages: true })
      .where({ shouldShowAppointments: false })
      .all()
      .then((res) => {
        if (pid) {
          setPackages(res.data.filter((datum) => datum.id.toString() === pid.toString() || datum.category === 'gift').sort(pricelistSort))
        } else if (senior) setPackages(res.data.filter((datum) => (datum.name.toLowerCase().includes('senior') || datum.category === 'gift') && !datum.isSitting))
        else setPackages(res.data.sort(pricelistSort))
      })
      .finally(() => setIsLoadingPackages(false))
  }, [hasPreviousOrders])

  useEffect(() => {
    const folders = studentImages.map(images => images.folder).filter(folder => !!folder)
    const years = folders.map(folder => folder.substr(folder.length - 4)).map(year => parseInt(year))

    setDisplayPastImages(isUsingAccessCode && years.some(year => year < currentYear()))
    setFetchingPastImages(false)
  }, [JSON.stringify({ studentImages })])

  useEffect(() => {
    if (fetchingPastImages) return
    if (packages.length === 0 || Object.keys(poseData).length === 0) return
    if (displayPastImages) return
    // The cart is created asynchronously (CustomerContext), so it can still be null
    // when packages + poses finish loading. Guard the dereference and re-run once the
    // cart exists (customer.cart is in the deps below) instead of throwing.
    if (!customer.cart) return

    const packageItem = packages[0];
    const orderPackage = new OrderPackage({
      packageId: packageItem.id,
      cartId: customer.cart.id,
      isPreviewHidden: !isUsingAccessCode,
    })

    setCustomer({ ...customer, currentOrderPackage: orderPackage })

    // TODO: uncomment when Alex wants auto-pushing back again
    // history.push(route(packageItem));

  }, [displayPastImages, packages, poseData, fetchingPastImages, customer.cart])

  const currentYear = () => {
    const date = new Date()
    const month = date.getMonth()
    const year = date.getFullYear()

    if (month >= 6) return year + 1

    return year
  }

  const route = (packageItem) => {
    let to = ''

    if (packageItem.category === 'gift') {
      to = routes.holiday.builder({ pid: pid });
    } else if (packageItem.isSeniorPortraits && !packageItem.isSitting) {
      to = routes.seniors.packageBuilder(packageItem.id);
      // to = routes.favoritePoseSelection(packageItem.id)
    } else if (packageItem.multiple && currentSeason && poseData[packageItem.id] && poseData[packageItem.id].length > 0) {
      to = routes.elementaryMultiPoseBuilder(packageItem.id)
    } else if (currentSeason && packageItem.backgroundSet && packageItem.backgroundSet.backgrounds.length > 0) {
      to = `/background_selection/${packageItem.id}`
    } else if (packageItem.multiple && poseData[packageItem.id]) {
      to = routes.elementaryMultiPoseBuilder(packageItem.id)
    } else if (packageItem.backgroundSet && packageItem.backgroundSet.backgrounds.length > 0) {
      to = `/background_selection/${packageItem.id}`
    } else {
      to = `/option_selection/${packageItem.id}`
    }

    return to;
  }

  const renderPackages = () => {
    if (fetching || isLoadingPackages || isFetchingPreviousOrders) {
      return (
        <div className={classes.spinnerContainer}>
          <Spinner color="blue" />
        </div>
      )
    }

    return (
      <Grid item container direction="column" spacing={3}>
        {packages.map((packageItem) => {
          return (
            <Grid item container key={packageItem.name}>
              <PackageItem
                packageItem={packageItem}
                to={route(packageItem)}
                currentSeason={currentSeason}
              />
            </Grid>
          )
        })}
        {showPastImagesCard && (
          <Grid item container key="past-images-quick-order">
            <PackageItem
              packageItem={{
                id: pastImagesQuickOrderPricelist.id,
                name: pastImagesQuickOrderPricelist.name || 'Pictures from Previous Years',
                bannerUrl: pastImagesQuickOrderPricelist.bannerUrl,
                imageUrl: pastImagesQuickOrderPricelist.imageUrl || prevYearsBanner,
                useNewPackageBuilder: !!pastImagesQuickOrderPricelist.bannerUrl,
                isSeniorPortraits: false,
              }}
              to={routes.pastImages.quickOrder()}
              currentSeason={currentSeason}
            />
          </Grid>
        )}
        {false && displayPastImages && !isOrderingAhead && (
          <Grid item>
            <PreviousYears student={currentStudent} />
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <div className={styles.outerContainer}>
      <Grid container direction="column" wrap="nowrap" classes={styles.pageContainer}>
        <Grid item classes={styles.instructionsContainer}>
          <Typography
            weight="inherit"
            align="center"
            classes={styles.headerText}>
            Choose one to view packages
          </Typography>
        </Grid>
        {renderPackages()}
      </Grid>
    </div>
  )
}

export default PackageSelection
